import { useCallback, useEffect, useState } from 'react';
import type { HardwareBuildItem, ReplaceBuildInput } from '@ddp/shared';

import { apiFetch } from '../../api/client.js';

/**
 * Nomenclature (BOM) d'un matériel (ADR-0030 §4). Charge les lignes de `GET /hardware/:id/build` et
 * les **remplace** en bloc via `PUT` (l'API est idempotente : rejouer le même envoi ne duplique
 * rien). `assetId` nul (aucun matériel sélectionné) ⇒ liste vide, sans appel.
 */
export function useBuild(assetId: string | null) {
  const [items, setItems] = useState<HardwareBuildItem[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!assetId) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      setItems(await apiFetch<HardwareBuildItem[]>(`/hardware/${assetId}/build`));
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [assetId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const save = useCallback(
    async (input: ReplaceBuildInput) => {
      if (!assetId) return;
      await apiFetch(`/hardware/${assetId}/build`, { method: 'PUT', body: input });
      await refresh();
    },
    [assetId, refresh],
  );

  return { items, loading, save };
}
